import { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Button, Input, Modal, Tabs } from "antd";
import { SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined, CopyOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import axios from "utils/axios";
import configFile from "config.json";
import BasicTab from "./tabs/list/BasicTab";
import ConfigTab from "./tabs/list/ConfigTab";
import { NotificationManager } from "react-notifications";

const API_BASE = `${configFile.PROD_SERVER_URL}/menu2`;
const tabItems = [
  { key: "basic", label: "Үндсэн мэдээлэл" },
  { key: "config", label: "Багцын тохиргоо" }
];

const resolveTagLabel = (tag) => {
  if (typeof tag === "string" || typeof tag === "number") {
    return String(tag).trim();
  }
  if (!tag || typeof tag !== "object") {
    return "";
  }
  const candidate = tag.slug ?? tag.name ?? tag.label ?? tag.title ?? tag.id ?? tag.value;
  if (typeof candidate === "string" || typeof candidate === "number") {
    return String(candidate).trim();
  }
  return "";
};

const getTagLabels = (tags) =>
  Array.isArray(tags)
    ? [...new Set(tags.map(resolveTagLabel).filter(Boolean))]
    : [];

const ComboPage = () => {
  const [query, setQuery] = useState("");
  const [combos, setCombos] = useState([]);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [activeTab, setActiveTab] = useState("basic");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [dealToDelete, setDealToDelete] = useState(null);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const navigate = useNavigate();

  const fetchCombos = useCallback(async () => {
    try {
      const { data: result } = await axios.post(`${API_BASE}/combo/getCombos`);
      setCombos(result?.data || []);
    } catch (err) {
      console.log(err);
      setCombos([]);
    }
  }, []);

  useEffect(() => {
    fetchCombos();
  }, [fetchCombos]);

  const filteredCombos = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return combos;
    }
    return combos.filter((deal) => {
      const name = deal?.masterName || "";
      const posId = String(deal?.comboMenuId || "");
      const tags = getTagLabels(deal?.tags).join(" ");
      return (
        name.toLowerCase().includes(q) ||
        posId.toLowerCase().includes(q) ||
        tags.toLowerCase().includes(q)
      );
    });
  }, [combos, query]);

  const openDeleteModal = useCallback((deal) => {
    setDealToDelete(deal);
    setDeleteModalOpen(true);
  }, []);

  const closeDeleteModal = useCallback(() => {
    setDeleteModalOpen(false);
    setDealToDelete(null);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!dealToDelete) {
      return;
    }
    await axios.post(`${API_BASE}/manageCombo/deleteCombo`, { comboId: dealToDelete.id });
    closeDeleteModal();
    setSelectedDeal(null);
    await fetchCombos();
  }, [closeDeleteModal, dealToDelete, fetchCombos]);

  const handleDuplicateCombo = useCallback(async () => {
    if (!selectedDeal?.id) {
      return;
    }
    setIsDuplicating(true);
    try {
      await axios.post(`${API_BASE}/manageCombo/duplicateCombo`, {
        comboId: selectedDeal.id,
      });
      await fetchCombos();
      NotificationManager.success("Амжилттай хууллаа");
    } catch (err) {
      NotificationManager.error(
        err?.response?.data?.errorMessage || "Хуулж чадсангүй",
        "Error",
        5000
      );
    } finally {
      setIsDuplicating(false);
    }
  }, [fetchCombos, selectedDeal]);

  return (
    <PageShell>
      <Modal
        title="Комбо устгах уу?"
        open={deleteModalOpen}
        onOk={confirmDelete}
        onCancel={closeDeleteModal}
        okText="Тийм"
        cancelText="Болих"
        okButtonProps={{ danger: true }}
      >
        <p>
          "{dealToDelete?.name || "Нэргүй комбо"}" комбо устгахдаа
          итгэлтэй байна уу?
        </p>
      </Modal>
      <ViewStack>
        <ViewPanel $state="list">
          <HeaderCard>
            <HeaderLeft>
              <Breadcrumb>Комбо / {selectedDeal?.name}</Breadcrumb>
            </HeaderLeft>
            <HeaderActions>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                size="middle"
                onClick={() => navigate("/deals/create")}
              >
                Комбо нэмэх
              </Button>
            </HeaderActions>
          </HeaderCard>

          <ContentGrid>
            <SidebarCard>
              <SearchRow>
                <Input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Комбо хайх"
                  prefix={<SearchOutlined />}
                  size="large"
                />
              </SearchRow>
              <SidebarHeader>Комбонууд</SidebarHeader>
              <DealList>
                {filteredCombos.length === 0 && (
                  <EmptyList>Комбо олдсонгүй.</EmptyList>
                )}
                {filteredCombos.map((deal) => {
                  const tagLabels = getTagLabels(deal.tags);
                  return (
                    <DealItem
                      key={deal.id || deal.comboMenuId || deal.masterName}
                      $active={deal.id === selectedDeal?.id}
                      onClick={() => setSelectedDeal(deal)}
                    >
                      <DealBody>
                        <DealName>{deal.name || "Нэргүй комбо"}</DealName>
                        {tagLabels.length > 0 && (
                          <DealTags>
                            {tagLabels.map((label) => (
                              <DealTag key={`${deal.id || deal.comboMenuId || deal.masterName}_${label}`}>
                                {label}
                              </DealTag>
                            ))}
                          </DealTags>
                        )}
                      </DealBody>
                    </DealItem>
                  );
                })}
              </DealList>
            </SidebarCard>

            <MainCard>
              {!selectedDeal && (
                <EmptyState>
                  <EmptyTitle>Комбо сонгоно уу</EmptyTitle>
                  <EmptyText>
                    Зүүн жагсаалтаас комбо сонгосны дараа дэлгэрэнгүй мэдээлэл энд харагдана.
                  </EmptyText>
                </EmptyState>
              )}
              {selectedDeal && (
                <>
                  <MainHeader>
                    <div>
                      <MainTitle>{selectedDeal?.name || selectedDeal?.masterName}</MainTitle>
                      <Subtitle>Комбоны тохиргоо</Subtitle>
                    </div>
                    <ActionRow>
                      <Button
                        icon={<CopyOutlined />}
                        onClick={handleDuplicateCombo}
                        loading={isDuplicating}
                        disabled={!selectedDeal?.id}
                      />
                      <Button
                        icon={<EditOutlined />}
                        onClick={() => navigate(`/deals/edit/${selectedDeal.id}`)}
                      />
                      <Button
                        icon={<DeleteOutlined />}
                        danger
                        onClick={() => openDeleteModal(selectedDeal)}
                      />
                    </ActionRow>
                  </MainHeader>

                  <TabsWrap
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={tabItems}
                  />
                  {activeTab === "basic" && (
                    <BasicTab
                      key={selectedDeal.id || selectedDeal.comboMenuId || selectedDeal.masterName}
                      deal={selectedDeal}
                      disabled
                    />
                  )}
                  {activeTab === "config" && (
                    <ConfigTab deal={selectedDeal} disabled />
                  )}
                </>
              )}
            </MainCard>
          </ContentGrid>
        </ViewPanel>
      </ViewStack>
    </PageShell>
  );
};

const PageShell = styled.section`
  --ink-900: #1f2a33;
  --ink-700: #3b4b5a;
  --ink-500: #6b7a88;
  --blue-700: #0958d9;
  --blue-600: #1677ff;
  --blue-500: #4096ff;
  --blue-200: #bae0ff;
  --card-bg: rgba(255, 255, 255, 0.94);
  --card-border: rgba(22, 119, 255, 0.18);
  --shadow-soft: 0 18px 50px rgba(33, 49, 63, 0.1);
  --shadow-float: 0 16px 30px rgba(22, 119, 255, 0.2);
  --radius-lg: 8px;
  --radius-md: 6px;
  --radius-sm: 5px;

  background: radial-gradient(circle at 15% 15%, rgba(22, 119, 255, 0.2), transparent 45%),
    radial-gradient(circle at 85% 25%, rgba(186, 224, 255, 0.45), transparent 55%),
    linear-gradient(140deg, #f5f8ff 0%, #eef3fb 55%, #f6f7fb 100%);
  font-family: "Sora", "Manrope", "SF Pro Display", sans-serif;
  color: var(--ink-900);
  letter-spacing: 0.01em;

  * {
    box-sizing: border-box;
  }

  .ant-btn,
  .ant-input,
  .ant-input-affix-wrapper,
  .ant-select-selector,
  .ant-picker,
  .ant-modal-content,
  .ant-modal-header,
  .ant-modal-footer,
  .ant-radio-button-wrapper,
  .ant-tag {
    border-radius: var(--radius-sm) !important;
  }
`;

const ViewStack = styled.div`
  position: relative;
  height: 100%;
`;

const ViewPanel = styled.div`
  position: ${({ $state }) => ($state === "edit" ? "absolute" : "relative")};
  inset: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 12px 16px 16px;
  transition: opacity 260ms ease, transform 320ms ease;
  opacity: ${({ $state, $active }) =>
    $state === "edit" ? ($active ? 1 : 0) : $active ? 0 : 1};
  transform: ${({ $state, $active }) => {
    if ($state === "edit") {
      return $active ? "translateX(0)" : "translateX(16px)";
    }
    return $active ? "translateX(-16px)" : "translateX(0)";
  }};
  pointer-events: ${({ $state, $active }) =>
    $state === "edit" ? ($active ? "auto" : "none") : $active ? "none" : "auto"};
`;

const HeaderCard = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 18px 22px;
  margin-bottom: 16px;
  border-radius: var(--radius-lg);
  background: var(--card-bg);
  box-shadow: var(--shadow-soft);
  border: 1px solid var(--card-border);
  backdrop-filter: blur(8px);
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Breadcrumb = styled.span`
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-size: 12px;
  color: var(--ink-500);
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;

  .ant-btn-primary {
    background: linear-gradient(135deg, var(--blue-600), var(--blue-500));
    border: none;
    box-shadow: var(--shadow-float);
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(280px, 0.95fr) minmax(0, 2.2fr);
  gap: 22px;
  flex: 1;
  min-height: 0;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const SidebarCard = styled.aside`
  background: var(--card-bg);
  border-radius: var(--radius-lg);
  padding: 18px;
  box-shadow: var(--shadow-soft);
  border: 1px solid var(--card-border);
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 800px;
`;

const SearchRow = styled.div`
  margin-bottom: 18px;

  .ant-input-affix-wrapper {
    border-radius: var(--radius-sm);
    border: 1px solid rgba(22, 119, 255, 0.25);
    padding: 8px 14px;
    box-shadow: inset 0 0 0 1px rgba(22, 119, 255, 0.05);
    background: rgba(255, 255, 255, 0.96);
  }
`;

const SidebarHeader = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--ink-700);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 12px;
`;

const DealList = styled.div`
  display: grid;
  gap: 12px;
  overflow: auto;
  min-height: 0;
  padding-top: 5px;
`;

const DealItem = styled.button`
  width: 100%;
  display: grid;
  gap: 6px;
  text-align: left;
  padding: 10px 12px;
  border-radius: var(--radius-md);
  border: 1px solid ${({ $active }) =>
    $active ? "rgba(22, 119, 255, 0.4)" : "rgba(148, 163, 184, 0.3)"};
  background: ${({ $active }) =>
    $active ? "rgba(22, 119, 255, 0.12)" : "rgba(255, 255, 255, 0.7)"};
  box-shadow: ${({ $active }) =>
    $active ? "0 16px 28px rgba(22, 119, 255, 0.18)" : "none"};
  cursor: pointer;
  transition: all 200ms ease;
  font: inherit;

  &:hover {
    border-color: rgba(22, 119, 255, 0.5);
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid rgba(22, 119, 255, 0.45);
    outline-offset: 2px;
  }

  .ant-tag {
    border: 1px solid rgba(22, 119, 255, 0.25);
    background: rgba(22, 119, 255, 0.08);
    color: var(--ink-700);
  }
`;

const DealName = styled.div`
  font-size: 16px;
  font-weight: 500;
  align-items: center;
`;

const DealBody = styled.div`
  display: grid;
  gap: 6px;
`;

const DealTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const DealTag = styled.span`
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid rgba(22, 119, 255, 0.25);
  background: rgba(22, 119, 255, 0.08);
  color: var(--ink-700);
  font-size: 11px;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;


const EmptyList = styled.div`
  padding: 12px 8px;
  color: var(--ink-500);
  font-size: 13px;
`;

const MainCard = styled.main`
  background: var(--card-bg);
  border-radius: var(--radius-lg);
  padding: 24px 26px;
  box-shadow: var(--shadow-soft);
  border: 1px solid var(--card-border);
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
`;

const EmptyState = styled.div`
  display: grid;
  place-items: center;
  height: 100%;
  min-height: 360px;
  text-align: center;
  color: var(--ink-500);
  gap: 8px;
`;

const EmptyTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--ink-700);
`;

const EmptyText = styled.p`
  margin: 0;
  max-width: 320px;
  font-size: 14px;
`;

const MainHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 18px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const MainTitle = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 600;
`;

const Subtitle = styled.p`
  margin: 8px 0 0;
  color: var(--ink-500);
  font-size: 14px;
`;

const ActionRow = styled.div`
  display: flex;
  gap: 10px;

  .ant-btn {
    border-radius: 6px;
    border-color: rgba(22, 119, 255, 0.2);
  }
`;

const TabsWrap = styled(Tabs)`
  margin-top: 8px;

  .ant-tabs-nav {
    margin-bottom: 18px;
  }

  .ant-tabs-tab {
    font-weight: 600;
    color: var(--ink-500);
  }

  .ant-tabs-tab-active .ant-tabs-tab-btn {
    color: var(--blue-600);
  }

  .ant-tabs-ink-bar {
    background: var(--blue-600);
  }
`;

export default ComboPage;
